import { API, get, patch, post, put } from "modules/api";
import { capitalize } from "lodash";
import { FormSubmitHandle } from "components/Forms/Form";
import Client, { ClientType } from "modules/parties/types/Client";
import { AddressSearchResultsType } from "modules/address/actions";
import QuestionnaireForm from "modules/workflow/components/QuestionnaireForm";
import {
  AnswersByQuestionnaireType,
  AnswersMap,
} from "modules/workflow/types/Answer";
import Questionnaire, {
  QuestionsMap,
  QuestionnaireID,
} from "modules/workflow/types/Questionnaire";
import React, { useCallback, useMemo, useState } from "react";
import getQuestionnaireFormInput from "screens/medicaidAnalysis/MedicaidLeadInterview/helpers";
import AnimatedLoadingOverlay from "components/AnimatedLoadingOverlay";
import { AnimatedLoadingOverlaySize } from "components/AnimatedLoadingOverlay/types";
import Address from "modules/parties/types/Address";
import styles from "./styles.module.scss";

export type PublicCompleteData = {
  answers: AnswersMap;
  clients: Record<string, Client>;
  unsavedAnswers: AnswersByQuestionnaireType;
  jurisdiction: string;
};

type Props = {
  publicLeadId: string;
  questionnaire: Questionnaire;
  questions: QuestionsMap;
  jurisdiction: string;
  initialClients?: Record<string, Client>;
  initialAnswers?: AnswersMap;
  initialPageIdx?: number;
  onComplete: (data: PublicCompleteData) => void;
};

const PublicMedicaidLeadInterview: React.FC<Props> = ({
  publicLeadId,
  questionnaire,
  questions,
  jurisdiction,
  initialClients,
  initialAnswers,
  initialPageIdx,
  onComplete,
}) => {
  const [answers, setAnswers] = useState<AnswersMap>(
    () => initialAnswers ?? {},
  );
  const [unsavedAnswers, setUnsavedAnswers] =
    useState<AnswersByQuestionnaireType>({});
  const [clients, setClients] = useState<Record<string, Client>>(
    () => initialClients ?? {},
  );
  const [pageIdx, setPageIdx] = useState(() => initialPageIdx ?? 0);

  // Mirrors createMedicaidLeadClient / saveMedicaidLeadClient using the public API
  const handleSaveClient = useCallback(
    (clientType: ClientType) =>
      async (
        clientId: string | undefined,
        clientDetails: Partial<Client>,
      ): Promise<void> => {
        try {
          const response = clientId
            ? await patch<{ client: Client }>(
                API.PUBLIC_MEDICAID_LEAD_CLIENT({ publicLeadId }),
                {
                  clientType: capitalize(clientType),
                  ...clientDetails,
                  clientId,
                },
              )
            : await put<{ client: Client }>(
                API.PUBLIC_MEDICAID_LEAD_CLIENT({ publicLeadId }),
                { clientType: capitalize(clientType), ...clientDetails },
              );
          setClients((prev) => ({
            ...prev,
            [clientType]: response.data.client,
          }));
        } catch {
          setClients((prev) => ({
            ...prev,
            [clientType]: {
              ...(prev[clientType] ?? {}),
              ...clientDetails,
              clientId: clientId || `public-${clientType}`,
            } as Client,
          }));
        }
      },
    [publicLeadId],
  );

  // Mirrors saveMedicaidLeadAnswers using the public API
  const handleSavePageAnswers = useCallback(
    async (_questionnaireId: QuestionnaireID, pageAnswers: AnswersMap) => {
      try {
        const response = await patch<{ validatedAnswers: AnswersMap }>(
          API.PUBLIC_MEDICAID_LEAD_ANSWERS({ publicLeadId }),
          { answers: pageAnswers, pageIdx },
        );
        setAnswers((prev) => ({
          ...prev,
          ...(response.data.validatedAnswers ?? pageAnswers),
        }));
      } catch {
        setAnswers((prev) => ({ ...prev, ...pageAnswers }));
      }
    },
    [publicLeadId, pageIdx],
  );

  const handleInterviewAnswersChanged = useCallback(
    (updated: AnswersByQuestionnaireType) => {
      setUnsavedAnswers((prev) => {
        const merged: AnswersByQuestionnaireType = { ...prev };
        for (const qId of Object.keys(updated)) {
          merged[qId] = { ...(prev[qId] || {}), ...(updated[qId] || {}) };
        }
        return merged;
      });
    },
    [],
  );

  const handlePageChanged = useCallback((idx: number): Promise<void> => {
    window.scrollTo(0, 0);
    setPageIdx(idx);
    return Promise.resolve();
  }, []);

  const handleAddressLookup = useCallback(
    async (addressId: string): Promise<Address | undefined> => {
      try {
        const response = await get<any>(API.PUBLIC_ADDRESS_LOOKUP(), {
          addressId,
        });
        const a = response.data;
        return {
          id: a.id,
          line1: a.line1,
          line2: a.line2,
          city: a.city,
          state: a.provinceCode,
          adminAreaName: a.adminAreaName,
          zip: a.zip,
        };
      } catch {
        return undefined;
      }
    },
    [],
  );

  const handleAddressSearch = useCallback(
    async (
      containerId?: string,
      searchTerm?: string,
    ): Promise<AddressSearchResultsType[] | undefined> => {
      try {
        const response = await get<any[]>(API.PUBLIC_ADDRESS_LOOKUP(), {
          containerId,
          searchTerm,
        });
        return response.data.map((a: any) => ({
          label: a.label,
          type: a.type,
          value: a.id,
        }));
      } catch {
        return undefined;
      }
    },
    [],
  );

  const handleConfirmSummary = useCallback(async () => {
    try {
      await post(API.PUBLIC_MEDICAID_LEAD_SUBMIT({ publicLeadId }), {});
    } catch {
      // proceed to confirmation even if status update fails
    }
    onComplete({ answers, clients, unsavedAnswers, jurisdiction });
  }, [
    onComplete,
    answers,
    clients,
    unsavedAnswers,
    jurisdiction,
    publicLeadId,
  ]);

  const handleNavigationRequest = useCallback(
    async (
      saveDelta: boolean,
      submitHandle: FormSubmitHandle | undefined,
      successCallback: () => any,
    ) => {
      try {
        if (submitHandle) await submitHandle(saveDelta);
        successCallback();
      } catch (_e) {}
    },
    [],
  );

  const supportedStates = useMemo(() => [jurisdiction], [jurisdiction]);

  const {
    questionnaireAnswers,
    questionnairePages,
    questionsBySubquestionnaire,
    clientsMap,
    questionnaireProgress,
  } = useMemo(
    () =>
      getQuestionnaireFormInput(
        questionnaire,
        questions,
        answers,
        unsavedAnswers,
        clients,
        null,
        handleSaveClient,
        handleAddressLookup,
        handleAddressSearch,
        handlePageChanged,
        handleConfirmSummary,
        supportedStates,
        true,
        true,
      ),
    [
      questionnaire,
      questions,
      answers,
      unsavedAnswers,
      clients,
      handleSaveClient,
      handleAddressLookup,
      handleAddressSearch,
      handlePageChanged,
      handleConfirmSummary,
      supportedStates,
    ],
  );

  if (
    questionnaireAnswers === undefined ||
    questionnairePages === undefined ||
    questionsBySubquestionnaire === undefined ||
    clientsMap === undefined ||
    questionnaireProgress === undefined
  ) {
    return (
      <div className={styles["container--centered"]}>
        <AnimatedLoadingOverlay size={AnimatedLoadingOverlaySize.LARGE} />
      </div>
    );
  }

  return (
    <QuestionnaireForm
      isAdmin={false}
      answers={questionnaireAnswers}
      questionnaireAnswers={questionnaireAnswers}
      questionnairePages={questionnairePages}
      questionsBySubquestionnaire={questionsBySubquestionnaire}
      onChange={handleInterviewAnswersChanged}
      onSavePageAnswers={handleSavePageAnswers}
      onPageChanged={handlePageChanged}
      clients={clientsMap}
      pageIdx={pageIdx}
      pageId=""
      questionnaireProgress={questionnaireProgress}
      handleNavigationRequest={handleNavigationRequest}
      showPeoplePrompt={false}
      editable={true}
    />
  );
};

export default PublicMedicaidLeadInterview;
